import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../api/client';
import { pickGoogleDoc, preloadDrivePicker, isPopupBlocked, isDriveScopeDenied } from '../../utils/googleDrivePicker';

const NAVY = '#0C2D48';
const ACCENT = '#5352ed';
const BORDER = '#e0e0e0';
const BG = '#f8f9fb';
const GREEN = '#27ae60';

// ── Shared: CRM update section ────────────────────────────────────────────────
function CRMUpdateSection({ crmSuggestions }) {
  const [checked, setChecked] = useState(() => new Set(crmSuggestions.map((_, i) => i)));
  const [updating, setUpdating] = useState(false);
  const [done, setDone] = useState(false);

  function toggle(i) {
    setChecked(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  }

  async function handleUpdate() {
    setUpdating(true);
    for (const [i, s] of crmSuggestions.entries()) {
      if (!checked.has(i)) continue;
      try {
        await api(`/api/contacts/${s.contactId}`, {
          method: 'PUT',
          body: JSON.stringify({
            lastContactedAt: new Date().toISOString(),
            notes: s.existingNotes ? `${s.existingNotes}\n${s.suggestNote}` : s.suggestNote,
          }),
        });
      } catch (_) {}
    }
    setUpdating(false);
    setDone(true);
  }

  if (done) {
    return (
      <div style={{ background: '#f6fff6', border: `1px solid #b8e6b8`, borderRadius: 8, padding: '8px 12px', fontSize: 12, color: GREEN, fontWeight: 700 }}>
        ✓ CRM updated
      </div>
    );
  }

  return (
    <div style={{ background: '#fff9f0', border: '1px solid #ffd7a0', borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Update CRM</div>
      {crmSuggestions.map((s, i) => (
        <label key={s.contactId} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={checked.has(i)} onChange={() => toggle(i)} style={{ marginTop: 2, accentColor: '#92400e', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 700 }}>{s.contactName}</div>
            <div style={{ fontSize: 11, color: '#666' }}>Set last contacted to today · "{s.suggestNote}"</div>
          </div>
        </label>
      ))}
      <button
        onClick={handleUpdate}
        disabled={updating || checked.size === 0}
        style={{ background: checked.size > 0 ? '#92400e' : '#ccc', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', marginTop: 4 }}
      >
        {updating ? 'Updating…' : `Update ${checked.size} contact${checked.size === 1 ? '' : 's'}`}
      </button>
    </div>
  );
}

// ── Shared: post-meeting result card ─────────────────────────────────────────
function PostMeetingCard({ result, onCreateTasks, onDismiss }) {
  const items = result.actionItems || [];
  const [checked, setChecked] = useState(() => new Set(items.map((_, i) => i)));
  const [editedItems, setEditedItems] = useState(items.map(x => ({ ...x })));
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);
  const allChecked = checked.size === items.length;

  function toggleAll() { setChecked(allChecked ? new Set() : new Set(items.map((_, i) => i))); }
  function toggle(i) { setChecked(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; }); }
  function updateField(i, field, val) {
    setEditedItems(prev => prev.map((item, idx) => idx === i ? { ...item, [field]: val } : item));
  }

  async function handleCreate() {
    setCreating(true);
    await onCreateTasks(editedItems.filter((_, i) => checked.has(i)));
    setCreated(true);
    setCreating(false);
  }

  const crmSuggestions = Array.isArray(result.crmSuggestions) ? result.crmSuggestions : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {result.noteTitle && (
        <div style={{ fontSize: 11, color: GREEN, fontWeight: 700 }}>✓ Saved to Notes: "{result.noteTitle}"</div>
      )}

      <div style={{ background: '#f0f4ff', border: `1px solid #c5d5f5`, borderRadius: 8, padding: '10px 12px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: ACCENT, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Summary</div>
        <div style={{ fontSize: 12, color: '#333', lineHeight: 1.55 }}>{result.summary}</div>
      </div>

      {Array.isArray(result.decisions) && result.decisions.length > 0 && (
        <div style={{ background: '#f6fff6', border: `1px solid #b8e6b8`, borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: GREEN, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Decisions</div>
          {result.decisions.map((d, i) => (
            <div key={i} style={{ fontSize: 12, color: '#333', marginBottom: 2, display: 'flex', gap: 5 }}>
              <span style={{ color: GREEN, fontWeight: 700, flexShrink: 0 }}>✓</span><span>{d}</span>
            </div>
          ))}
        </div>
      )}

      {items.length > 0 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: NAVY, textTransform: 'uppercase', letterSpacing: 0.5 }}>Action Items</div>
            <button onClick={toggleAll} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: ACCENT, fontWeight: 600 }}>
              {allChecked ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {editedItems.map((item, i) => (
              <label key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, padding: '7px 9px', borderRadius: 7, border: `1px solid ${checked.has(i) ? ACCENT : BORDER}`, background: checked.has(i) ? '#f4f3ff' : '#fafafa', cursor: 'pointer' }}>
                <input type="checkbox" checked={checked.has(i)} onChange={() => toggle(i)} style={{ marginTop: 3, accentColor: ACCENT, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <input
                    value={item.task}
                    onChange={e => updateField(i, 'task', e.target.value)}
                    style={{ fontSize: 12, fontWeight: 600, color: '#222', border: 'none', background: 'transparent', outline: 'none', width: '100%', padding: 0, fontFamily: 'inherit' }}
                  />
                  <div style={{ display: 'flex', gap: 5 }}>
                    {item.assignee && <span style={{ fontSize: 10, color: ACCENT, fontWeight: 600 }}>{item.assignee}</span>}
                    {item.dueDate && <span style={{ fontSize: 10, color: '#888' }}>by {item.dueDate}</span>}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {crmSuggestions.length > 0 && <CRMUpdateSection crmSuggestions={crmSuggestions} />}

      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
        {items.length > 0 && !created && (
          <button
            onClick={handleCreate}
            disabled={creating || checked.size === 0}
            style={{ background: checked.size > 0 ? NAVY : '#ccc', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: creating ? 0.7 : 1 }}
          >
            {creating ? 'Creating…' : `Create ${checked.size} task${checked.size === 1 ? '' : 's'}`}
          </button>
        )}
        {created && <span style={{ fontSize: 12, color: GREEN, fontWeight: 700, alignSelf: 'center' }}>✓ Tasks created</span>}
        <button onClick={onDismiss} style={{ background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '7px 13px', fontSize: 12, cursor: 'pointer', color: '#999' }}>
          Done
        </button>
      </div>
    </div>
  );
}

// ── Shared: recording card ─────────────────────────────────────────────────────
function RecordingCard({ recording, idKey, processingId, results, onProcess, onCreateTasks, onDismissResult }) {
  const id = recording[idKey];
  const result = results[id];
  const isProcessing = processingId === id;
  const hasResult = result && !result.error;

  return (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 10, marginBottom: 12, overflow: 'hidden', background: '#fff' }}>
      <div style={{ padding: '10px 12px', background: BG, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {recording.meetingTitle}
          </div>
          <div style={{ fontSize: 11, color: '#888', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {recording.date && <span>{recording.date}</span>}
            {recording.duration && <span>{recording.duration} min</span>}
            <span style={{ color: GREEN, fontWeight: 600 }}>✓ Transcript available</span>
          </div>
        </div>
        {!hasResult && (
          <button
            onClick={() => onProcess(recording)}
            disabled={isProcessing || !!processingId}
            style={{
              background: isProcessing ? '#ddd' : ACCENT,
              color: isProcessing ? '#aaa' : '#fff',
              border: 'none', borderRadius: 7,
              padding: '6px 12px', fontSize: 11, fontWeight: 700,
              cursor: isProcessing || processingId ? 'default' : 'pointer',
              whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >
            {isProcessing ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 10, height: 10, border: '2px solid rgba(255,255,255,0.5)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'mtSpin 0.8s linear infinite' }} />
                Processing…
              </span>
            ) : 'Process with Aidey'}
          </button>
        )}
        {hasResult && (
          <span style={{ fontSize: 11, color: GREEN, fontWeight: 700, alignSelf: 'center', flexShrink: 0 }}>
            ✓ Processed{result._via === 'picker' ? ' · via Picker' : ''}
          </span>
        )}
      </div>

      {result?.error && (
        <div style={{ padding: '8px 12px', fontSize: 11, color: '#e05252', display: 'flex', alignItems: 'center', gap: 8 }}>
          Failed: {result.error}
          <button onClick={() => onProcess(recording)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: ACCENT, fontSize: 11, fontWeight: 600 }}>Retry</button>
        </div>
      )}

      {hasResult && (
        <div style={{ padding: '10px 12px' }}>
          <PostMeetingCard
            result={result}
            onCreateTasks={(items) => onCreateTasks(recording, items)}
            onDismiss={() => onDismissResult(id)}
          />
        </div>
      )}
    </div>
  );
}

// ── Platform panel hook ───────────────────────────────────────────────────────
function usePlatformPanel(endpoint, idKey) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [processingId, setProcessingId] = useState(null);
  const [results, setResults] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api(endpoint);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.error || 'Failed to load recordings');
        return;
      }
      const data = await res.json();
      setRecordings(data.recordings || []);
    } catch {
      setError('Could not reach the server');
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  // resolveTranscript (optional): async fn returning either the transcript text, or an object
  // { transcriptText, source } so the caller can record WHICH path produced it. Zoom/Teams pass no
  // resolver and use the text inline on the recording. Google Meet (Block 178) resolves at process
  // time: the Drive Picker is PRIMARY and reports source:'picker'; if it's cancelled/fails, the
  // resolver falls back to the Drive-search transcript and reports source:'drive-search'. `source`
  // is stored on the result as `_via` so the UI can show which path actually ran. A null/empty
  // return means nothing to process (e.g. a standalone pick cancelled) → abort quietly.
  async function process(recording, processEndpoint, resolveTranscript) {
    const id = recording[idKey];
    setProcessingId(id);
    try {
      let transcriptText, via;
      if (resolveTranscript) {
        const r = await resolveTranscript(recording);
        if (!r) { setProcessingId(null); return; }               // cancelled / nothing → degrade, no error
        if (typeof r === 'string') transcriptText = r;
        else { transcriptText = r.transcriptText; via = r.source; }
      } else {
        transcriptText = recording.transcriptText;
      }
      if (!transcriptText) { setProcessingId(null); return; }
      const res = await api(processEndpoint, {
        method: 'POST',
        body: JSON.stringify({
          [idKey]: id,
          transcriptText,
          meetingTitle: recording.meetingTitle,
          attendees: recording.attendees || [],
        }),
      });
      if (!res.ok) throw new Error('Processing failed');
      const data = await res.json();
      setResults(prev => ({ ...prev, [id]: via ? { ...data, _via: via } : data }));
      window.dispatchEvent(new Event('aidey:notesUpdated'));
    } catch (err) {
      setResults(prev => ({ ...prev, [id]: { error: err.message } }));
    } finally {
      setProcessingId(null);
    }
  }

  function dismissResult(id) {
    setResults(prev => { const n = { ...prev }; delete n[id]; return n; });
  }

  // Add a recording to the top of the list if it isn't already there (used when a user picks a
  // transcript straight from Drive that wasn't among the resolved candidates — Route 3 standalone).
  function upsertRecording(rec) {
    setRecordings(prev => prev.some(r => r[idKey] === rec[idKey]) ? prev : [rec, ...prev]);
  }

  return { loading, error, recordings, processingId, results, load, process, dismissResult, upsertRecording };
}

// ── Shared: platform tab body ─────────────────────────────────────────────────
function PlatformTabBody({ loading, error, recordings, processingId, results, idKey, emptyNote, emptyHint, connectNote, onLoad, onProcess, onCreateTasks, onDismissResult, onPickFromDrive, pickLabel, pickError }) {
  const pickBtn = (extraStyle) => onPickFromDrive ? (
    <button onClick={() => onPickFromDrive()} style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', ...extraStyle }}>
      {pickLabel || '📂 Choose transcript from Google Drive'}
    </button>
  ) : null;
  const pickErr = pickError ? (
    <div style={{ fontSize: 11, color: '#e05252', marginTop: 8 }}>{pickError}</div>
  ) : null;

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: 30, color: '#aaa' }}>
        <div style={{ width: 28, height: 28, border: `3px solid ${ACCENT}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'mtSpin 0.8s linear infinite' }} />
        <div style={{ fontSize: 12 }}>Fetching transcripts…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '18px 14px', textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: '#e05252', marginBottom: 6 }}>{error}</div>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>{connectNote}</div>
        <button onClick={onLoad} style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Retry</button>
      </div>
    );
  }

  if (recordings.length === 0) {
    return (
      <div style={{ padding: '24px 14px', textAlign: 'center', color: '#aaa', fontSize: 12 }}>
        <div style={{ marginBottom: 4 }}>{emptyNote}</div>
        <div style={{ fontSize: 11, color: '#bbb', marginTop: 6 }}>{emptyHint}</div>
        {onPickFromDrive && <div style={{ marginTop: 14 }}>{pickBtn()}</div>}
        {pickErr}
        <div><button onClick={onLoad} style={{ marginTop: 14, background: 'none', border: `1px solid ${BORDER}`, borderRadius: 7, padding: '6px 14px', fontSize: 11, color: '#999', cursor: 'pointer' }}>Refresh</button></div>
      </div>
    );
  }

  return (
    <div>
      {onPickFromDrive && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginBottom: 10 }}>
          {pickBtn({ padding: '5px 12px', fontSize: 11 })}
          {pickErr}
        </div>
      )}
      {recordings.map(recording => (
        <RecordingCard
          key={recording[idKey]}
          recording={recording}
          idKey={idKey}
          processingId={processingId}
          results={results}
          onProcess={onProcess}
          onCreateTasks={onCreateTasks}
          onDismissResult={onDismissResult}
        />
      ))}
    </div>
  );
}

// ── Zoom tab ──────────────────────────────────────────────────────────────────
function ZoomTab() {
  const { loading, error, recordings, processingId, results, load, process, dismissResult } =
    usePlatformPanel('/api/meetings/zoom/recent-transcripts', 'meetingId');

  useEffect(() => { load(); }, [load]);

  async function createTasks(recording, items) {
    for (const item of items) {
      try {
        await api('/api/tasks', { method: 'POST', body: JSON.stringify({ title: item.task, priority: 'Medium', taskType: 'Task', date: item.dueDate || new Date().toISOString().slice(0, 10), details: `Action item from Zoom meeting: ${recording.meetingTitle}${item.assignee ? `. Assigned to: ${item.assignee}` : ''}` }) });
      } catch (_) {}
    }
    window.dispatchEvent(new Event('aidey:tasksUpdated'));
  }

  return (
    <PlatformTabBody
      loading={loading} error={error} recordings={recordings}
      processingId={processingId} results={results} idKey="meetingId"
      emptyNote="No Zoom cloud recordings with transcripts found in the last 7 days."
      emptyHint="Make sure cloud recording and transcription are enabled in your Zoom account settings."
      connectNote="Go to Settings → Video meetings to connect your Zoom account."
      onLoad={load}
      onProcess={(rec) => process(rec, '/api/meetings/zoom/process-transcript')}
      onCreateTasks={createTasks}
      onDismissResult={dismissResult}
    />
  );
}

// ── Google Meet tab ───────────────────────────────────────────────────────────
function GoogleMeetTab() {
  const { loading, error, recordings, processingId, results, load, process, dismissResult, upsertRecording } =
    usePlatformPanel('/api/meetings/google-meet/recent-transcripts', 'fileId');
  const [pickError, setPickError] = useState('');

  useEffect(() => { load(); }, [load]);
  // Warm the Google scripts when the panel opens so the pick click keeps its user-gesture (pop-up).
  useEffect(() => { preloadDrivePicker(); }, []);

  async function createTasks(recording, items) {
    for (const item of items) {
      try {
        await api('/api/tasks', { method: 'POST', body: JSON.stringify({ title: item.task, priority: 'Medium', taskType: 'Task', date: item.dueDate || new Date().toISOString().slice(0, 10), details: `Action item from Google Meet: ${recording.meetingTitle}${item.assignee ? `. Assigned to: ${item.assignee}` : ''}` }) });
      } catch (_) {}
    }
    window.dispatchEvent(new Event('aidey:tasksUpdated'));
  }

  // Block 178 commit 2: the Drive search is gone (it needed drive.readonly). The Google Picker
  // (drive.file) is now the ONLY way to bring in a transcript, on both the standalone "Choose from
  // Drive" action and a card's "Process" button. One path, no fallback:
  //   • recording given (a card) → Picker pre-navigated to its fileId; recording null → unnavigated.
  //   • pick → export by fileId with the drive.file token → process the text.
  // A pick failure surfaces a real message (pop-up blocked / Drive permission / generic) and resets;
  // it never hangs and never claims transcripts appear automatically (they don't anymore).
  async function pickAndProcess(recording) {
    setPickError('');
    let picked;
    try { picked = await pickGoogleDoc({ fileIds: recording?.fileId ? [recording.fileId] : undefined }); }
    catch (e) {
      setPickError(
        isPopupBlocked(e)
          ? 'Google could not open its sign-in window. Your browser may be blocking pop-ups for this site. Allow pop-ups and try again.'
        : isDriveScopeDenied(e)
          ? 'Aidey needs permission to open the file you choose from Google Drive. When Google asks, tick the Google Drive box and try again.'
          : 'Could not open Google Drive. Please try again.');
      return;
    }
    if (!picked) return; // user cancelled — quietly do nothing

    const rec = {
      fileId: picked.fileId,
      meetingTitle: recording?.meetingTitle || picked.name || 'Meeting transcript',
      date: recording?.date || '',
    };
    upsertRecording(rec);
    await process(rec, '/api/meetings/google-meet/process-transcript', async () => {
      const res = await api('/api/meetings/google-meet/export-transcript', {
        method: 'POST',
        body: JSON.stringify({ fileId: picked.fileId, accessToken: picked.accessToken }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Could not read that transcript.'); }
      const d = await res.json();
      if (!d.transcriptText) throw new Error('That document had no readable text.');
      return { transcriptText: d.transcriptText, source: 'picker' };
    });
  }

  return (
    <PlatformTabBody
      loading={loading} error={error} recordings={recordings}
      processingId={processingId} results={results} idKey="fileId"
      emptyNote="Pick a Google Meet transcript from your Google Drive to summarise it."
      emptyHint="Google Meet saves a transcript to your Drive when transcription is turned on during the call. Choose it below and Aidey will pull out the summary, decisions and action items."
      connectNote="Connect Google in Settings → Video meetings to access your Meet transcripts."
      onLoad={load}
      onProcess={pickAndProcess}
      onCreateTasks={createTasks}
      onDismissResult={dismissResult}
      onPickFromDrive={() => pickAndProcess(null)}
      pickError={pickError}
      pickLabel="📂 Choose transcript from Google Drive"
    />
  );
}

// ── Teams tab ─────────────────────────────────────────────────────────────────
function TeamsTab() {
  const { loading, error, recordings, processingId, results, load, process, dismissResult } =
    usePlatformPanel('/api/meetings/teams/recent-transcripts', 'meetingId');

  useEffect(() => { load(); }, [load]);

  async function createTasks(recording, items) {
    for (const item of items) {
      try {
        await api('/api/tasks', { method: 'POST', body: JSON.stringify({ title: item.task, priority: 'Medium', taskType: 'Task', date: item.dueDate || new Date().toISOString().slice(0, 10), details: `Action item from Teams meeting: ${recording.meetingTitle}${item.assignee ? `. Assigned to: ${item.assignee}` : ''}` }) });
      } catch (_) {}
    }
    window.dispatchEvent(new Event('aidey:tasksUpdated'));
  }

  const needsReconnect = error?.includes('reconnect') || error?.includes('token expired');
  const transcriptScope = error?.includes('transcript') || error?.includes('403');

  return (
    <PlatformTabBody
      loading={loading} error={error} recordings={recordings}
      processingId={processingId} results={results} idKey="meetingId"
      emptyNote="No Teams meeting transcripts found in the last 7 days."
      emptyHint="Transcripts are only available for meetings you organised. Teams must have transcription enabled in your meeting policy."
      connectNote={needsReconnect
        ? 'Reconnect Microsoft in Settings → Video meetings to refresh your Teams access.'
        : transcriptScope
          ? 'Transcript access may require your Teams admin to enable transcription in your meeting policy.'
          : 'Connect Microsoft in Settings → Video meetings to access your Teams transcripts.'}
      onLoad={load}
      onProcess={(rec) => process(rec, '/api/meetings/teams/process-transcript')}
      onCreateTasks={createTasks}
      onDismissResult={dismissResult}
    />
  );
}

// ── Upload tab ────────────────────────────────────────────────────────────────
function UploadTab() {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [pasteText, setPasteText] = useState('');
  const [meetingTitle, setMeetingTitle] = useState('');
  const [attendees, setAttendees] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(false);

  const hasContent = file || pasteText.trim().length > 10;

  function handleFileSelect(e) {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setPasteText(''); setError(null); }
  }

  function handleDrop(e) {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) { setFile(f); setPasteText(''); setError(null); }
  }

  function clearFile() {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleProcess() {
    if (!hasContent) return;
    if (!meetingTitle.trim()) { setError('Please enter a meeting title.'); return; }
    setError(null);
    setProcessing(true);
    setResult(null);

    try {
      let res;
      if (file) {
        const formData = new FormData();
        formData.append('transcript', file);
        formData.append('meetingTitle', meetingTitle.trim());
        if (attendees.trim()) formData.append('attendees', attendees.trim());
        res = await api('/api/meetings/process-transcript', { method: 'POST', body: formData });
      } else {
        res = await api('/api/meetings/process-transcript', {
          method: 'POST',
          body: JSON.stringify({
            transcriptText: pasteText.trim(),
            meetingTitle: meetingTitle.trim(),
            attendees: attendees.trim(),
          }),
        });
      }

      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Processing failed');
      }
      const data = await res.json();
      setResult(data);
      window.dispatchEvent(new Event('aidey:notesUpdated'));
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  }

  async function createTasks(items) {
    for (const item of items) {
      try {
        await api('/api/tasks', { method: 'POST', body: JSON.stringify({ title: item.task, priority: 'Medium', taskType: 'Task', date: item.dueDate || new Date().toISOString().slice(0, 10), details: `Action item from meeting: ${meetingTitle}${item.assignee ? `. Assigned to: ${item.assignee}` : ''}` }) });
      } catch (_) {}
    }
    setCreated(true);
    window.dispatchEvent(new Event('aidey:tasksUpdated'));
  }

  function reset() {
    setFile(null);
    setPasteText('');
    setMeetingTitle('');
    setAttendees('');
    setResult(null);
    setError(null);
    setCreated(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  if (result) {
    return (
      <div>
        <PostMeetingCard result={result} onCreateTasks={createTasks} onDismiss={reset} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Meeting title */}
      <div>
        <label style={{ fontSize: 11, fontWeight: 700, color: NAVY, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Meeting Title <span style={{ color: '#e05252' }}>*</span>
        </label>
        <input
          value={meetingTitle}
          onChange={e => setMeetingTitle(e.target.value)}
          placeholder="e.g. Q3 Planning Meeting"
          style={{ width: '100%', border: `1px solid ${BORDER}`, borderRadius: 7, padding: '7px 10px', fontSize: 12, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
        />
      </div>

      {/* Attendees */}
      <div>
        <label style={{ fontSize: 11, fontWeight: 700, color: NAVY, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Attendees <span style={{ color: '#aaa', fontWeight: 400, textTransform: 'none' }}>(optional, comma-separated)</span>
        </label>
        <input
          value={attendees}
          onChange={e => setAttendees(e.target.value)}
          placeholder="e.g. Sarah, James, Marketing team"
          style={{ width: '100%', border: `1px solid ${BORDER}`, borderRadius: 7, padding: '7px 10px', fontSize: 12, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
        />
      </div>

      {/* File drop zone */}
      <div>
        <label style={{ fontSize: 11, fontWeight: 700, color: NAVY, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Upload File
        </label>
        <div
          onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => !file && fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${isDragging ? ACCENT : file ? GREEN : BORDER}`,
            borderRadius: 8,
            padding: '16px 12px',
            textAlign: 'center',
            background: isDragging ? '#f4f3ff' : file ? '#f6fff6' : BG,
            cursor: file ? 'default' : 'pointer',
            transition: 'all 0.15s',
          }}
        >
          {file ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>📄</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#333' }}>{file.name}</span>
              <button
                onClick={e => { e.stopPropagation(); clearFile(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 14, lineHeight: 1 }}
              >×</button>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 22, marginBottom: 4 }}>📂</div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Drop a file here or <span style={{ color: ACCENT, fontWeight: 600 }}>browse</span></div>
              <div style={{ fontSize: 11, color: '#aaa' }}>.vtt · .txt · .docx — max 10 MB</div>
            </>
          )}
          <input ref={fileInputRef} type="file" accept=".vtt,.txt,.docx" onChange={handleFileSelect} style={{ display: 'none' }} />
        </div>
      </div>

      {/* OR divider + paste area */}
      {!file && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, height: 1, background: BORDER }} />
            <span style={{ fontSize: 11, color: '#bbb', fontWeight: 600 }}>OR PASTE TRANSCRIPT</span>
            <div style={{ flex: 1, height: 1, background: BORDER }} />
          </div>

          <textarea
            value={pasteText}
            onChange={e => setPasteText(e.target.value)}
            placeholder="Paste your meeting transcript here…"
            rows={7}
            style={{ width: '100%', border: `1px solid ${BORDER}`, borderRadius: 7, padding: '8px 10px', fontSize: 12, fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.5 }}
          />
          {pasteText.trim().length > 0 && (
            <div style={{ fontSize: 11, color: '#aaa', marginTop: -8 }}>{pasteText.trim().split(/\s+/).length} words</div>
          )}
        </>
      )}

      {error && <div style={{ fontSize: 12, color: '#e05252', background: '#fff5f5', border: '1px solid #ffd0d0', borderRadius: 7, padding: '7px 10px' }}>{error}</div>}

      <button
        onClick={handleProcess}
        disabled={!hasContent || !meetingTitle.trim() || processing}
        style={{
          background: hasContent && meetingTitle.trim() ? NAVY : '#ccc',
          color: '#fff', border: 'none', borderRadius: 8,
          padding: '10px 18px', fontSize: 13, fontWeight: 700,
          cursor: hasContent && meetingTitle.trim() && !processing ? 'pointer' : 'default',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          opacity: processing ? 0.8 : 1,
        }}
      >
        {processing ? (
          <>
            <span style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'mtSpin 0.8s linear infinite' }} />
            Processing with Aidey…
          </>
        ) : 'Process with Aidey'}
      </button>
    </div>
  );
}

// ── Main MeetingTranscripts panel ─────────────────────────────────────────────
const TABS = [
  { id: 'zoom',   label: 'Zoom',        icon: '🎬' },
  { id: 'meet',   label: 'Google Meet', icon: '📹' },
  { id: 'teams',  label: 'Teams',       icon: '💼' },
  { id: 'upload', label: 'Upload',      icon: '📂' },
];

export default function MeetingTranscripts({ onDismiss }) {
  const [activeTab, setActiveTab] = useState('zoom');

  return (
    <div style={{ fontFamily: "'Raleway', sans-serif", display: 'flex', flexDirection: 'column', height: '100%' }}>
      <style>{`@keyframes mtSpin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ padding: '10px 14px 0', borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: NAVY }}>Meeting Transcripts</div>
            <div style={{ fontSize: 11, color: '#888' }}>Last 7 days · auto-fetch &amp; process · upload any transcript</div>
          </div>
          {onDismiss && (
            <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 17, color: '#aaa', padding: 4, lineHeight: 1 }}>✕</button>
          )}
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 0, overflowX: 'auto' }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                background: 'none', border: 'none',
                borderBottom: activeTab === tab.id ? `2px solid ${ACCENT}` : '2px solid transparent',
                padding: '6px 12px', fontSize: 11,
                fontWeight: activeTab === tab.id ? 700 : 500,
                color: activeTab === tab.id ? ACCENT : '#888',
                cursor: 'pointer', fontFamily: "'Raleway', sans-serif",
                transition: 'color 0.15s', display: 'flex', alignItems: 'center', gap: 4,
                whiteSpace: 'nowrap',
              }}
            >
              <span>{tab.icon}</span><span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
        {activeTab === 'zoom'   && <ZoomTab />}
        {activeTab === 'meet'   && <GoogleMeetTab />}
        {activeTab === 'teams'  && <TeamsTab />}
        {activeTab === 'upload' && <UploadTab />}
      </div>
    </div>
  );
}
